import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { mergeRegister } from '@lexical/utils';
import { Box, List, ListItemButton, ListItemText, Paper } from '@mui/material';
import { COMMAND_PRIORITY_NORMAL, KEY_ARROW_DOWN_COMMAND, KEY_ARROW_UP_COMMAND } from 'lexical';
import { ReactNode, useEffect, useRef } from 'react';
import { valueTypeColor } from './typeColors';

/** The badge looks: functions, fields (every field source shares the field look) and bound names. */
export type TypeaheadBadge = 'fn' | 'm' | 'c' | 'r' | 's' | 'f' | 'k' | 'n';

export interface TypeaheadItem {
  key: string;
  primary: string;
  /** Indices into `primary` of fuzzy-matched characters, emphasized in the render. */
  primaryMatches?: number[];
  /** Leading kind marker: fn (function), m (metric field), c (company field), f (formula field), n (bound name). */
  badge?: TypeaheadBadge;
  /** Right-aligned value type ("number", "string", …), coloured like the editor's literal highlights. */
  type?: string;
  /** Shown in the side panel while the row is highlighted: full doc / description. */
  detail?: ReactNode;
}

interface TypeaheadMenuProps<TOption extends MenuOption> {
  options: TOption[];
  toItem: (option: TOption) => TypeaheadItem;
  selectedIndex: number | null;
  onHighlight: (index: number) => void;
  onSelect: (option: TOption) => void;
}

/** Contiguous matched runs render emphasized; everything else passes through. */
export function renderHighlightedText(text: string, matches?: number[]): ReactNode {
  if (!matches?.length) {
    return text;
  }
  const matched = new Set(matches);
  const segments: ReactNode[] = [];
  let start = 0;
  for (let i = 1; i <= text.length; i++) {
    if (i === text.length || matched.has(i) !== matched.has(start)) {
      const chunk = text.slice(start, i);
      segments.push(
        matched.has(start) ? (
          <Box
            key={start}
            component='span'
            sx={{ fontWeight: 700, textDecoration: 'underline', color: 'inherit' }}
          >
            {chunk}
          </Box>
        ) : (
          chunk
        )
      );
      start = i;
    }
  }
  return segments;
}

/**
 * The entity's own fields — metrics on a company, rounds, securities, and the
 * formulas built over them — wear the app's purple.
 */
const ENTITY_BADGE_SX = {
  bgcolor: 'var(--mui-colors-secondary-10)',
  color: 'var(--mui-colors-secondary-70)',
} as const;

/**
 * Fields are filled rounded squares; company attributes and KPIs each get
 * their own tint, since a mixed list is mostly the entity's own fields and
 * colouring the two that come from elsewhere is what makes it scannable.
 * A function is not a value, so it wears a different shape rather than a
 * seventh colour: an outlined blue circle. A name bound by LET or LAMBDA is
 * the formula's own, not a field of anything, so it stays neutral grey.
 */
const BADGE_SX = {
  fn: {
    borderRadius: '50%',
    border: '1px solid var(--mui-colors-primary-50)',
    color: 'var(--mui-colors-primary-60)',
  },
  m: ENTITY_BADGE_SX,
  r: ENTITY_BADGE_SX,
  s: ENTITY_BADGE_SX,
  f: ENTITY_BADGE_SX,
  c: { bgcolor: 'var(--mui-colors-success-20)', color: 'var(--mui-colors-success-70)' },
  k: { bgcolor: 'var(--mui-colors-warning-20)', color: 'var(--mui-colors-warning-60)' },
  n: { bgcolor: 'var(--mui-colors-neutral-20)', color: 'var(--mui-colors-neutral-50)' },
} as const;

function Badge({ badge }: { badge: TypeaheadBadge }) {
  return (
    <Box
      component='span'
      sx={{
        borderRadius: '4px',
        ...BADGE_SX[badge],
        // Square, so the circle is round; flex-centred so the outlined
        // variant's border does not push its label off-centre.
        width: '1.125rem',
        height: '1.125rem',
        boxSizing: 'border-box',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        mr: '8px',
        fontSize: '0.625rem',
        fontWeight: 700,
      }}
    >
      {badge}
    </Box>
  );
}

/** Tallest the menu ever gets when there is room for it: about nine rows. */
const MAX_HEIGHT_PX = 288;
/** Breathing room kept between the menu's bottom edge and the window. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * Shared MUI renderer for both typeahead menus: single-line rows with a kind
 * badge, plus a side panel documenting the highlighted row (IDE-style).
 * Rendered into the caret-anchored element the typeahead plugin portals to;
 * the z-index must clear the modal dialog since that anchor lives on
 * document.body.
 */
export function TypeaheadMenu<TOption extends MenuOption>({
  options,
  toItem,
  selectedIndex,
  onHighlight,
  onSelect,
}: TypeaheadMenuProps<TOption>) {
  const [editor] = useLexicalComposerContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Move the highlight ourselves. Lexical's own arrow handlers (registered at
  // LOW, so ours pre-empt them) reveal the new row with the DOM's
  // scrollIntoView, which walks every scrollable ancestor up to the document —
  // arrowing past the menu's fold scrolled the whole page out from under the
  // caret-anchored menu.
  useEffect(() => {
    const move = (delta: number) => (event: KeyboardEvent | null) => {
      if (!options.length) {
        return false;
      }
      event?.preventDefault();
      // Wraps in both directions, as the plugin's own handlers did.
      onHighlight(((selectedIndex ?? 0) + delta + options.length) % options.length);
      return true;
    };
    return mergeRegister(
      editor.registerCommand(KEY_ARROW_DOWN_COMMAND, move(1), COMMAND_PRIORITY_NORMAL),
      editor.registerCommand(KEY_ARROW_UP_COMMAND, move(-1), COMMAND_PRIORITY_NORMAL)
    );
  }, [editor, options.length, selectedIndex, onHighlight]);

  // The menu hangs off the caret and could otherwise reach past the bottom of
  // the window, leaving the rows below the fold unreachable now that nothing
  // scrolls the page. Cap both panes at the room actually below them instead.
  // Deferred to a microtask so the measurement sees the anchor where the
  // typeahead plugin's own effect — which runs after this one — put it.
  useEffect(() => {
    let cancelled = false;
    const clamp = () => {
      const container = containerRef.current;
      if (cancelled || !container) {
        return;
      }
      const room = window.innerHeight - container.getBoundingClientRect().top - VIEWPORT_MARGIN_PX;
      const maxHeight = Math.max(Math.min(room, MAX_HEIGHT_PX), 0);
      for (const pane of Array.from(container.children)) {
        (pane as HTMLElement).style.maxHeight = `${maxHeight}px`;
      }
    };
    queueMicrotask(clamp);
    window.addEventListener('resize', clamp);
    return () => {
      cancelled = true;
      window.removeEventListener('resize', clamp);
    };
  });

  // Keep the highlighted row visible by scrolling the list, and only the list.
  useEffect(() => {
    const list = listRef.current;
    const row = selectedIndex != null ? options[selectedIndex]?.ref?.current : null;
    if (!list || !row) {
      return;
    }
    const top = row.offsetTop;
    const bottom = top + row.offsetHeight;
    if (top < list.scrollTop) {
      list.scrollTop = top;
    } else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [selectedIndex, options]);

  const detail = selectedIndex != null ? toItem(options[selectedIndex])?.detail : null;

  return (
    <Box ref={containerRef} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Paper
        ref={listRef}
        elevation={4}
        sx={{
          minWidth: '16rem',
          maxWidth: '28rem',
          maxHeight: `${MAX_HEIGHT_PX}px`,
          overflowY: 'auto',
          position: 'relative',
          zIndex: (theme) => theme.zIndex.modal + 10,
        }}
      >
        <List dense disablePadding>
          {options.map((option, index) => {
            const item = toItem(option);
            return (
              <ListItemButton
                key={item.key}
                selected={index === selectedIndex}
                ref={(element) => option.setRefElement(element)}
                onMouseEnter={() => onHighlight(index)}
                // Keep focus in the editor so selecting doesn't dismiss the caret
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onSelect(option)}
                // Half MUI's default 16px gutter — the menu hangs off the
                // caret, so the width belongs to the names rather than the
                // margins. Explicit px: the theme's spacing unit is 2px, so
                // shorthand numbers here read four times larger than they are.
                sx={{ px: '8px' }}
              >
                {item.badge && <Badge badge={item.badge} />}
                <ListItemText primary={renderHighlightedText(item.primary, item.primaryMatches)} />
                {item.type && (
                  <Box
                    component='span'
                    sx={{
                      ml: 1,
                      flexShrink: 0,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: valueTypeColor(item.type) ?? 'text.secondary',
                    }}
                  >
                    {item.type}
                  </Box>
                )}
              </ListItemButton>
            );
          })}
        </List>
      </Paper>
      {detail != null && (
        <Paper
          elevation={4}
          sx={{
            minWidth: '14rem',
            maxWidth: '20rem',
            maxHeight: `${MAX_HEIGHT_PX}px`,
            overflowY: 'auto',
            position: 'relative',
            zIndex: (theme) => theme.zIndex.modal + 10,
            p: 1.5,
          }}
        >
          {detail}
        </Paper>
      )}
    </Box>
  );
}
